import { sourceAttribute } from "../i18n/LocalizedCopy";
import { formattingLocale } from "../i18n/formattingLocale";
import { LocalizedText, LocalizedElement, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  archiveConciergeSportDraft,
  fetchConciergeSportDraft,
  fetchConciergeSportFollowUp,
  participateConciergeSport,
  recordConciergeMetric,
  saveConciergeSportDraft,
  searchConciergeSport,
  findConciergeReplacements,
} from "./conciergeApi";
import ConciergeGroupPanel from "./ConciergeGroupPanel";
import ConciergeDayPanel from "./ConciergeDayPanel";

const QUESTIONS = ["city", "planned_on", "distance_km", "level", "accessibility", "participants"];
const ACTIVITY_OPTIONS = [
  ["", "Tous les sports"],
  ["course", "Course"],
  ["football", "Football"],
  ["fitness", "Fitness"],
  ["natation", "Natation"],
  ["randonnée", "Randonnée"],
  ["vélo", "Vélo"],
  ["yoga", "Yoga"],
];

function localDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat(formattingLocale(), { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function nextDateFromLabel(label) {
  const normalized = String(label || "").toLowerCase();
  const now = new Date();
  if (normalized === "aujourd’hui" || normalized === "ce soir") return now.toISOString().slice(0, 10);
  if (normalized === "demain") {
    now.setDate(now.getDate() + 1);
    return now.toISOString().slice(0, 10);
  }
  const days = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
  const target = days.indexOf(normalized);
  if (target < 0) return "";
  const delta = (target - now.getDay() + 7) % 7;
  now.setDate(now.getDate() + delta);
  return now.toISOString().slice(0, 10);
}

function initialFilters(facts = {}) {
  return {
    city: facts.city || "",
    activity_kind: facts.sport || "",
    planned_on: nextDateFromLabel(facts.date),
    distance_km: 10,
    travel_mode: "walk",
    level: "all",
    accessibility: "any",
    participants: Number(facts.capacity || 1),
  };
}

function QuestionField({ name, values, onChange, origin, locating, onUseLocation }) {
  useSourceLanguage();
  if (name === "city") {
    return <label><span><LocalizedText source={"Dans quelle ville ou zone ?"} /></span><LocalizedElement elementType={"input"} copyAttributes={["placeholder"]} autoFocus value={values.city} maxLength={120} onChange={(event) => onChange("city", event.target.value)} placeholder={sourceAttribute("Ex. Lyon")} /></label>;
  }
  if (name === "planned_on") {
    return <label><span><LocalizedText source={"À quelle date ?"} /></span><input autoFocus type="date" value={values.planned_on || ""} onChange={(event) => onChange("planned_on", event.target.value)} /></label>;
  }
  if (name === "distance_km") {
    return <div className="et-concierge-sport__distance"><label><span><LocalizedText source={"Jusqu’à quelle distance ?"} /></span><select autoFocus value={values.distance_km} onChange={(event) => onChange("distance_km", Number(event.target.value))}><LocalizedElement elementType={"option"} copyChildren value={2}>2 km</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value={5}>5 km</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value={10}>10 km</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value={25}>25 km</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value={50}>50 km</LocalizedElement></select></label><label><span><LocalizedText source={"Mode de trajet"} /></span><select value={values.travel_mode} onChange={(event) => onChange("travel_mode", event.target.value)}><LocalizedElement elementType={"option"} copyChildren value="walk">À pied</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="bike">À vélo</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="drive">En voiture</LocalizedElement></select></label><button type="button" className="is-secondary" disabled={locating} onClick={onUseLocation}>{locating ? <LocalizedText source={"Localisation…"} /> : origin ? <LocalizedText source={"Point de départ activé ✓"} /> : <LocalizedText source={"Utiliser ma position pour le rayon réel"} />}</button><small><LocalizedText source={"Votre position sert uniquement à cette recherche et n’est pas enregistrée dans le brouillon."} /></small></div>;
  }
  if (name === "level") {
    return <label><span><LocalizedText source={"Quel niveau souhaitez-vous ?"} /></span><select autoFocus value={values.level} onChange={(event) => onChange("level", event.target.value)}><LocalizedElement elementType={"option"} copyChildren value="all">Tous niveaux</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="beginner">Débutant</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="intermediate">Intermédiaire</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="advanced">Avancé</LocalizedElement></select></label>;
  }
  if (name === "accessibility") {
    return <label><span><LocalizedText source={"Avez-vous un besoin d’accessibilité à filtrer ?"} /></span><select autoFocus value={values.accessibility} onChange={(event) => onChange("accessibility", event.target.value)}><LocalizedElement elementType={"option"} copyChildren value="any">Pas de filtre</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="mobility">Mobilité</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="sensory">Sensoriel</LocalizedElement><LocalizedElement elementType={"option"} copyChildren value="assistance">Accompagnement</LocalizedElement></select><small><LocalizedText source={"Ce choix structuré n’est enregistré que si vous sauvegardez le brouillon."} /></small></label>;
  }
  return <label><span><LocalizedText source={"Combien de participants ?"} /></span><input autoFocus type="number" min="1" max="20" value={values.participants} onChange={(event) => onChange("participants", Math.max(1, Math.min(20, Number(event.target.value) || 1)))} /></label>;
}

function participationAction(activity) {
  const participation = activity.viewer_participation || {};
  if (participation.booking) return participation.can_cancel ? ["cancel", "Annuler mon inscription"] : ["", "Inscription active"];
  if (participation.waitlist?.status === "notified") return ["book", "Confirmer la place proposée"];
  if (participation.waitlist) return ["leave_waitlist", "Quitter la liste d’attente"];
  if (participation.can_book) return ["book", "Réserver et ajouter à l’agenda"];
  return ["join_waitlist", "Rejoindre la liste d’attente"];
}

function ActivityResult({ activity, selected, busy, groupMode, onSelect, onOpen, onParticipate }) {
  useSourceLanguage();
  const conflicts = Array.isArray(activity.agenda_conflicts) ? activity.agenda_conflicts : [];
  const [action, actionLabel] = participationAction(activity);
  return (
    <article className={`et-concierge-result${selected ? " is-selected" : ""}`}>
      <header><div><small>{activity.category}<LocalizedText source={" · gratuit"} /></small><h4>{activity.title}</h4></div><strong>{activity.remaining_places}<LocalizedText source={" place"} />{activity.remaining_places > 1 ? "s" : ""}</strong></header>
      <p>{activity.public_area} · {activity.city} · {localDate(activity.starts_at)}</p>
      <div className="et-concierge-result__facts">
        <span><LocalizedText source={"Niveau : "} />{activity.experience_level === "unknown" ? <LocalizedText source={"non précisé"} /> : activity.experience_level}</span>
        <span>{activity.accessibility?.available ? <LocalizedText source={"Accessibilité renseignée"} /> : <LocalizedText source={"Accessibilité à confirmer"} />}</span>
        <span>{activity.distance?.label}</span>
      </div>
      {activity.match_reasons?.length ? <details><summary><LocalizedText source={"Pourquoi cette proposition ?"} /></summary><ul>{activity.match_reasons.map((reason) => <li key={reason.kind}>{reason.label}</li>)}</ul></details> : null}
      {conflicts.length ? <div className="et-concierge-result__conflict" role="note"><strong><LocalizedText source={"Conflit avec votre agenda"} /></strong>{conflicts.map((item) => <span key={item.id}>{item.title} · {localDate(item.starts_at)}</span>)}</div> : <p className="et-concierge-result__available">{activity.agenda_checked ? <LocalizedText source={"Aucun conflit détecté dans votre agenda."} /> : <LocalizedText source={"Agenda non vérifié : connectez-vous pour contrôler vos disponibilités."} />}</p>}
      <footer><button type="button" className="is-secondary" onClick={() => onSelect(activity)}>{selected ? <LocalizedText source={"Activité choisie"} /> : <LocalizedText source={"Choisir et suivre"} />}</button><button type="button" className="is-secondary" onClick={() => onOpen(activity.concierge_url)}><LocalizedText source={"Voir la fiche"} /></button>{groupMode ? <span><LocalizedText source={"Proposez cette activité depuis le panneau du groupe. Chaque personne confirmera sa place."} /></span> : <button type="button" disabled={!action || busy} onClick={() => onParticipate(activity, action)}>{busy ? <LocalizedText source={"Traitement…"} /> : actionLabel}</button>}</footer>
    </article>
  );
}

function JourneyFollowUp({ journey, onOpen }) {
  useSourceLanguage();
  if (!journey) return null;
  return (
    <section className="et-concierge-follow" aria-labelledby="et-concierge-follow-title">
      <header><small><LocalizedText source={"Fiche de suivi unique"} /></small><h4 id="et-concierge-follow-title">{journey.activity.title}</h4></header>
      <ol>{journey.steps.map((step) => <li key={step.id} className={`is-${step.status}`}><span aria-hidden="true">{step.status === "completed" ? "✓" : step.status === "locked" ? "·" : "→"}</span><div><strong>{step.title}</strong><p>{step.description}</p></div>{step.url ? <button type="button" onClick={() => onOpen(step.url)}><LocalizedText source={"Ouvrir"} /></button> : null}</li>)}</ol>
    </section>
  );
}

export default function SportConciergeJourney({ facts, isLoggedIn, onOpen }) {
  useSourceLanguage();
  const [values, setValues] = useState(() => initialFilters(facts));
  const [questionIndex, setQuestionIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [rankingNotice, setRankingNotice] = useState("");
  const [resultFilters, setResultFilters] = useState(null);
  const [replacementMode, setReplacementMode] = useState(false);
  const [participationQuantity, setParticipationQuantity] = useState(null);
  const [groupSearchMode, setGroupSearchMode] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [draftVersion, setDraftVersion] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyActivityId, setBusyActivityId] = useState("");
  const [origin, setOrigin] = useState(null);
  const [locating, setLocating] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const recordedMetrics = useRef(new Set());

  const record = useCallback((event) => {
    if (recordedMetrics.current.has(event)) return;
    recordedMetrics.current.add(event);
    recordConciergeMetric(event).catch(() => null);
  }, []);

  const loadFollowUp = useCallback((activityId, signal) => {
    if (!isLoggedIn || !activityId) return Promise.resolve();
    return fetchConciergeSportFollowUp(activityId, { signal }).then((payload) => {
      setJourney(payload?.journey || null);
      (payload?.journey?.metric_events || []).forEach(record);
    });
  }, [isLoggedIn, record]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const controller = new AbortController();
    fetchConciergeSportDraft({ signal: controller.signal }).then((payload) => {
      const draft = payload?.draft;
      if (!draft) return;
      setValues((current) => ({ ...current, city: draft.city, activity_kind: draft.activity_kind, planned_on: draft.planned_on || "", distance_km: draft.distance_km, travel_mode: draft.travel_mode || "walk", level: draft.level, accessibility: draft.accessibility, participants: draft.participants }));
      setDraftVersion(draft.version);
      setSelectedId(draft.selected_activity_id || "");
      setNotice("Brouillon repris depuis votre compte.");
      if (draft.selected_activity_id) loadFollowUp(draft.selected_activity_id, controller.signal).catch(() => null);
    }).catch((reason) => {
      if (reason?.name !== "AbortError" && reason?.status !== 404) setError(reason.message);
    });
    return () => controller.abort();
  }, [isLoggedIn, loadFollowUp]);

  const progress = Math.round(((questionIndex + 1) / QUESTIONS.length) * 100);
  const update = (name, value) => setValues((current) => ({ ...current, [name]: value }));
  const canContinue = QUESTIONS[questionIndex] !== "city" || values.city.trim().length >= 2;
  const draftPayload = useMemo(() => ({ ...values, city: values.city.trim(), planned_on: values.planned_on || null }), [values]);
  const filtersPayload = useMemo(() => ({
    ...draftPayload,
    ...(origin ? { origin_latitude: origin.latitude, origin_longitude: origin.longitude } : {}),
  }), [draftPayload, origin]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("La géolocalisation n’est pas disponible sur cet appareil.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setOrigin({
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
        });
        setLocating(false);
        setNotice("Point de départ activé pour cette recherche uniquement.");
      },
      () => {
        setLocating(false);
        setError("Votre position n’a pas été partagée. La recherche par ville reste disponible.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const search = async (explicitFilters = filtersPayload, bookingQuantity = explicitFilters.participants, forGroup = false) => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const payload = await searchConciergeSport(explicitFilters);
      setResults(Array.isArray(payload?.results) ? payload.results : []);
      setAdjustments(payload?.adjustments || []);
      setRankingNotice(payload?.ranking_notice || "");
      setResultFilters(explicitFilters);
      setParticipationQuantity(bookingQuantity);
      setGroupSearchMode(forGroup);
      setReplacementMode(false);
      const countNotice = payload?.results?.length ? `${payload.results.length} activité(s) disponible(s).` : "Aucune activité ne correspond encore à ces critères.";
      setNotice(`${countNotice} ${payload?.distance_notice || ""}`.trim());
      record("results_shown");
    } catch (reason) {
      setError(reason.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async (activityId = selectedId) => {
    if (!isLoggedIn) {
      onOpen(`/login?next=${encodeURIComponent("/")}`);
      return null;
    }
    setLoading(true);
    setError("");
    try {
      const payload = await saveConciergeSportDraft({
        ...draftPayload,
        selected_activity_id: activityId || null,
        ...(draftVersion !== null ? { expected_version: draftVersion } : {}),
      });
      setDraftVersion(payload.draft.version);
      setSelectedId(payload.draft.selected_activity_id || "");
      setNotice("Brouillon enregistré sur votre compte. Vous pourrez le reprendre sur un autre appareil.");
      record("draft_saved");
      return payload.draft;
    } catch (reason) {
      setError(reason.code === "concierge_draft_conflict" ? "Le brouillon a changé sur un autre appareil. Rechargez la page pour reprendre sa dernière version." : reason.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const participate = async (activity, action) => {
    if (!isLoggedIn) {
      onOpen(`/login?next=${encodeURIComponent(activity.concierge_url)}`);
      return;
    }
    const quantity = participationQuantity || (resultFilters || filtersPayload).participants;
    const messages = {
      book: `Confirmer ${quantity} place(s) et ajouter cette activité à votre agenda avec des rappels ?`,
      join_waitlist: `Rejoindre la liste d’attente pour ${quantity} place(s) ?`,
      cancel: "Annuler votre inscription ? Les places seront immédiatement proposées à la liste d’attente.",
      leave_waitlist: "Quitter cette liste d’attente ?",
    };
    if (typeof window !== "undefined" && !window.confirm(messages[action])) return;
    setBusyActivityId(activity.id);
    setError("");
    try {
      const payload = await participateConciergeSport(activity.id, action, quantity);
      setSelectedId(activity.id);
      setJourney(payload?.journey || null);
      if (action === "book") record("booking_confirmed");
      if (action === "join_waitlist") record("waitlist_joined");
      if (action === "cancel") record("booking_cancelled");
      const refreshed = await searchConciergeSport(resultFilters || filtersPayload);
      setResults(Array.isArray(refreshed?.results) ? refreshed.results : []);
      await save(activity.id);
      setNotice({
        book: "Inscription confirmée. L’activité et ses rappels sont dans votre agenda.",
        join_waitlist: "Liste d’attente rejointe. Une place libérée vous sera proposée de façon déterministe.",
        cancel: "Inscription annulée. Les places ont été libérées et l’agenda synchronisé.",
        leave_waitlist: "Vous avez quitté la liste d’attente.",
      }[action]);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusyActivityId("");
    }
  };

  const select = async (activity) => {
    setSelectedId(activity.id);
    setJourney(null);
    record("activity_selected");
    if (isLoggedIn) {
      const saved = await save(activity.id);
      if (saved) await loadFollowUp(activity.id).catch((reason) => setError(reason.message));
    } else {
      setJourney({ activity, steps: [
        { id: "found", title: "Activité trouvée", status: "completed", description: activity.title, url: activity.concierge_url },
        { id: "booking", title: "Inscription", status: "current", description: "Connectez-vous pour réserver et conserver le suivi.", url: `/login?next=${encodeURIComponent(activity.concierge_url)}` },
        ...["Discussion", "Rappel", "Présence", "Avis"].map((title) => ({ id: title.toLowerCase(), title, status: "locked", description: "Disponible après l’inscription.", url: "" })),
      ] });
    }
  };

  const archive = async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      await archiveConciergeSportDraft();
      setDraftVersion(null);
      setSelectedId("");
      setJourney(null);
      setResults([]);
      setAdjustments([]);
      setNotice("Brouillon archivé.");
    } catch (reason) {
      setError(reason.message);
    } finally {
      setLoading(false);
    }
  };

  const applyAdjustment = (proposal) => {
    if (!window.confirm(`${proposal.label} ? Seuls les critères indiqués seront modifiés.`)) return;
    const next = { ...(resultFilters || filtersPayload), ...proposal.changes };
    const { origin_latitude, origin_longitude, ...structured } = next;
    setValues(structured);
    search(next, participationQuantity || next.participants, groupSearchMode);
  };

  const searchForGroup = (group, slot) => {
    const start = new Date(slot.starts_at);
    const day = slot.search_date || `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
    const next = { ...filtersPayload, city: group.city, planned_on: day, participants: group.eligible_participants };
    setValues((current) => ({ ...current, city: group.city, planned_on: day, participants: group.eligible_participants }));
    setQuestionIndex(QUESTIONS.length - 1);
    search(next, 1, true);
  };

  const followActivity = (activityId) => {
    setSelectedId(activityId);
    loadFollowUp(activityId).catch((reason) => setError(reason.message));
  };

  const findReplacements = async (activityId) => {
    setLoading(true); setError("");
    try {
      const payload = await findConciergeReplacements(activityId, filtersPayload);
      setResults(payload.results || []); setAdjustments(payload.adjustments || []);
      setResultFilters(payload.filters || filtersPayload); setReplacementMode(true);
      setParticipationQuantity(payload.booking_quantity || 1); setGroupSearchMode(false);
      setNotice(payload.notice || "Aucune réservation effectuée. Choisissez une alternative puis confirmez.");
    } catch (reason) { setError(reason.message); }
    finally { setLoading(false); }
  };

  return (
    <section className="et-concierge-sport" aria-labelledby="et-concierge-sport-title">
      <ConciergeGroupPanel isLoggedIn={isLoggedIn} results={results} onSearchSlot={searchForGroup} onFollowActivity={followActivity} onOpen={onOpen} />
      <header><div><small><LocalizedText source={"Parcours Sport guidé · 6 questions fixes"} /></small><h4 id="et-concierge-sport-title"><LocalizedText source={"Précisons votre recherche"} /></h4></div><span>{questionIndex + 1}/{<LocalizedText source={QUESTIONS.length} />}</span></header>
      <div className="et-concierge-sport__progress" aria-label={`Progression ${progress} %`}><i style={{ width: `${progress}%` }} /></div>
      <div className="et-concierge-sport__question">
        <QuestionField name={QUESTIONS[questionIndex]} values={values} onChange={update} origin={origin} locating={locating} onUseLocation={useCurrentLocation} />
        {questionIndex === 0 ? <label className="et-concierge-sport__kind"><span><LocalizedText source={"Type d’activité"} /></span><select value={values.activity_kind} onChange={(event) => update("activity_kind", event.target.value)}>{ACTIVITY_OPTIONS.map(([value, label]) => <LocalizedElement elementType={"option"} copyChildren key={value || "all"} value={value}>{label}</LocalizedElement>)}</select></label> : null}
      </div>
      <div className="et-concierge-sport__controls">
        <button type="button" disabled={questionIndex === 0 || loading} onClick={() => setQuestionIndex((value) => Math.max(0, value - 1))}><LocalizedText source={"Précédent"} /></button>
        {questionIndex < QUESTIONS.length - 1 ? <button type="button" className="is-primary" disabled={!canContinue || loading} onClick={() => setQuestionIndex((value) => Math.min(QUESTIONS.length - 1, value + 1))}><LocalizedText source={"Question suivante"} /></button> : <button type="button" className="is-primary" disabled={loading} onClick={() => search()}>{loading ? <LocalizedText source={"Recherche…"} /> : <LocalizedText source={"Voir les activités"} />}</button>}
      </div>
      {error ? <p className="et-concierge-sport__error" role="alert">{<LocalizedText source={error} />}</p> : null}
      {notice ? <p className="et-concierge-sport__notice" role="status">{<LocalizedText source={notice} />}</p> : null}
      {adjustments.length ? <LocalizedElement elementType={"section"} copyAttributes={["aria-label"]} className="et-concierge-adjustments" aria-label={sourceAttribute("Autres recherches possibles")}><h4><LocalizedText source={"Aucun résultat ? Voici ce que vous pouvez changer"} /></h4><p><LocalizedText source={"Ces propositions ne garantissent pas de disponibilité. Aucun filtre ne change sans votre accord."} /></p>{adjustments.map((proposal) => <button key={proposal.id} type="button" disabled={loading} onClick={() => applyAdjustment(proposal)}>{proposal.label}</button>)}</LocalizedElement> : null}
      {results.length ? <div className="et-concierge-results"><header><h4>{replacementMode ? <LocalizedText source={"Activités de remplacement — à confirmer"} /> : <LocalizedText source={"Activités disponibles"} />}</h4><button type="button" disabled={loading} onClick={() => save()}>{isLoggedIn ? <LocalizedText source={"Enregistrer ce parcours"} /> : <LocalizedText source={"Se connecter pour enregistrer"} />}</button></header><p>{rankingNotice}</p><div>{results.map((activity) => <ActivityResult key={activity.id} activity={activity} selected={selectedId === activity.id} busy={busyActivityId === activity.id} groupMode={groupSearchMode} onSelect={select} onOpen={onOpen} onParticipate={participate} />)}</div></div> : null}
      <JourneyFollowUp journey={journey} onOpen={onOpen} />
      {isLoggedIn && selectedId ? <ConciergeDayPanel key={`${selectedId}:${journey?.booking?.status || ""}`} activityId={selectedId} onOpen={onOpen} onChanged={followActivity} onFindAlternatives={findReplacements} /> : null}
      {draftVersion !== null ? <button type="button" className="et-concierge-sport__archive" disabled={loading} onClick={archive}><LocalizedText source={"Archiver ce brouillon"} /></button> : null}
      <p className="et-concierge-sport__truth"><LocalizedText source={"La phrase initiale du Concierge n’est pas enregistrée. Les groupes et réponses sont enregistrés après votre validation. Les mesures restent des compteurs quotidiens agrégés."} /></p>
    </section>
  );
}
